import { getLang } from './i18n';
import { deviceName, isConnected, keyName, netLabelOf, pinName, pinNode, placedOrder, potentialOf, wireNode, type Analysis } from './analyze';
import { catOf, MAKER, parseTerms, type PartLookup, partText } from './catalog';
import { bbWorld, loc, onSeg, pinsW, SHEET } from './geometry';
import { rangeOf, ratedOf } from './generate';
import { fnOf, isMainPoles, LIB, PLUG_TYPES } from './library';
import { symbolName } from './custom';
import { copperCutList, cutText, doorHoles, layoutIssues, layoutMaterials } from './layout';
import { cabinetWiring, panel3dIssues, WIRE_RESERVE } from './panel3d';
import { plcIssues, plcList } from './plc';
import { boardVerification } from './verify';
import type { Placed, Project, Wire } from './types';
import { t as $t, tk } from './i18n';


export type Severity = 'err' | 'warn';
export interface CheckMessage {
  sev: Severity;
  msg: string;
  /** symbol the message is about; absent for page-level issues (panel layout) */
  it?: Placed;
  /** page index when there is no symbol */
  pi?: number;
  /** the rule that raised it: its (untranslated) message template, the same in every language */
  rule?: string;
  /** stable identity for ignoring it: rule, target and its order among the same rule's messages on the target */
  key?: string;
}

/** Short hash of a text (FNV-1a), for compact check keys. */
const hash = (s: string) => {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 0x01000193);
  return (h >>> 0).toString(36);
};

export const checkLocation = (c: CheckMessage) => (c.it ? '/' + loc(c.it) : c.pi != null ? `/${c.pi + 1}` : '');

/**
 * Current a copper conductor carries continuously, by cross-section in mm²: power cables laid by method C at
 * 30 °C (as the generators size them), control wiring after IEC 60204-1.
 */
const CARRY: [number, number][] = [
  [0.5, 3], [0.75, 7.6], [1, 10.4], [1.5, 15], [2.5, 21], [4, 28], [6, 36], [10, 50], [16, 68], [25, 89], [35, 110], [50, 134], [70, 171], [95, 207], [120, 239], [150, 272], [185, 310], [240, 364],
];
/** Protective devices by symbol: their rated current from the part ("C16", "160 A"); motor protection by the
 * lower end of its setting range (it is set to the motor's current) */
const PROTECTS: Record<string, 'rated' | 'range'> = { cb1: 'rated', cb3n: 'rated', mccb3: 'rated', cb3m: 'rated', fuse: 'rated', fsd3: 'rated', rcbo2: 'rated', cb3: 'range' };
/** Wires of a page at a point: one per end lying on it, two for a wire running through it. */
function wiresAt(A: Analysis, pi: number, p: [number, number]): { w: Wire; n: number }[] {
  const near = (q: number[]) => Math.abs(q[0] - p[0]) < 0.01 && Math.abs(q[1] - p[1]) < 0.01;
  const out: { w: Wire; n: number }[] = [];
  for (const w of A.pr.pages[pi]?.wires ?? []) {
    const ends = (near(w.pts[0]) ? 1 : 0) + (w.pts.length > 1 && near(w.pts[w.pts.length - 1]) ? 1 : 0);
    if (ends) out.push({ w, n: ends });
    else if (w.pts.slice(1).some((q, k) => onSeg(p[0], p[1], w.pts[k], q))) out.push({ w, n: 2 });
  }
  return out;
}

/** Plug devices (by device key) and their pins. */
export function plugsOf(A: Analysis): Map<string, Placed[]> {
  const m = new Map<string, Placed[]>();
  for (const it of A.all)
    if (PLUG_TYPES.has(it.s.type)) {
      const key = A.devKey.get(it.s.id) ?? '-?';
      (m.get(key) || m.set(key, []).get(key)!).push(it);
    }
  return m;
}
/** The connection points joined to one pin of a symbol, comma separated. */
function connectedTo(A: Analysis, it: Placed, i: number): string {
  const r = A.uf.find(pinNode(it.s, i)), o: string[] = [];
  for (const x of A.all) {
    const role = LIB[x.s.type].role;
    if (x.s.id === it.s.id || role === 'brk' || role === 'text') continue;
    LIB[x.s.type].pins.forEach((_, j) => {
      if (A.uf.find(pinNode(x.s, j)) === r) o.push(pinName(A, x.s, j));
    });
  }
  return o.join(', ');
}

/** Design-rule check run ("Kontrol"). `parts` enables contact-capacity checks. */
export function runChecks(A: Analysis, parts: PartLookup = catOf, o: { ignored?: boolean } = {}): CheckMessage[] {
  const out: CheckMessage[] = [];
  // function templates: contacts used vs. contacts the assigned part provides
  for (const [key, list] of A.mains) {
    const tag = keyName(A, key).replace(/^-/, '');
    const part = list[0].s.part ? parts(list[0].s.part) : undefined;
    if (!part) continue;
    // with terminals in the template: every contact drawn is one of them, and none twice
    const tpl = parseTerms(part.terms);
    if (tpl.length) {
      const known = new Set(tpl.map((c) => c.pins.join('-'))), seen = new Set<string>();
      for (const it of A.auxs.get(key) || []) {
        if (isMainPoles(it.s.type) || !LIB[it.s.type].kind && it.s.type !== 'co') continue;
        const pins = (A.auxLabels.get(it.s.id) || []).join('-');
        if (!known.has(pins)) out.push({ sev: 'err', rule: "-{0}: {1} kontağı {2} parçasında yok", msg: $t("-{0}: {1} kontağı {2} parçasında yok", tag, pins, part.pn), it });
        else if (seen.has(pins)) out.push({ sev: 'err', rule: "-{0}: {1} kontağı birden fazla kez kullanılmış", msg: $t("-{0}: {1} kontağı birden fazla kez kullanılmış", tag, pins), it });
        seen.add(pins);
      }
    }
    const used = { no: 0, nc: 0 };
    for (const it of A.auxs.get(key) || []) {
      const k = LIB[it.s.type].kind;
      if (it.s.type === 'co') {
        // a changeover contact uses one NO and one NC of the device
        used.no++;
        used.nc++;
      } else if (!isMainPoles(it.s.type) && k) used[k]++;
    }
    for (const k of ['no', 'nc'] as const) {
      const cap = part[k];
      if (cap != null && used[k] > cap)
        out.push({ sev: 'err', rule: "-{0}: {1} {2} kontak kullanılmış, {3} yalnızca {4} tane sağlıyor", msg: $t("-{0}: {1} {2} kontak kullanılmış, {3} yalnızca {4} tane sağlıyor", tag, used[k], k.toUpperCase(), part.pn, cap), it: list[0] });
    }
  }
  // cables
  const cableTags = new Map<string, Placed>();
  for (const c of A.cables.values()) {
    const s = c.it.s;
    const nm = deviceName(A, s), key = A.devKey.get(s.id) ?? '';
    if (!c.cross.length) out.push({ sev: 'warn', rule: "{0}: kablo hiçbir telin üzerinden geçmiyor", msg: $t("{0}: kablo hiçbir telin üzerinden geçmiyor", nm), it: c.it });
    const over = c.cross.filter((x) => !x.core).length;
    if (over) out.push({ sev: 'err', rule: "{0}: {1} tel geçiyor ama kabloda {2} damar var", msg: $t("{0}: {1} tel geçiyor ama kabloda {2} damar var", nm, c.cross.length, c.cores.length), it: c.it });
    if (key && cableTags.has(key)) out.push({ sev: 'err', rule: "{0}: kablo etiketi birden fazla kez kullanılmış", msg: $t("{0}: kablo etiketi birden fazla kez kullanılmış", nm), it: c.it });
    const cp = s.part ? parts(s.part) : undefined;
    if (cp?.cores && cp.cores.split(',').map((x) => x.trim()).join(',') !== c.cores.join(','))
      out.push({ sev: 'warn', rule: '{0}: damarlar seçilen parçadan ({1}) farklı', msg: $t('{0}: damarlar seçilen parçadan ({1}) farklı', nm, cp.pn), it: c.it });
    cableTags.set(key, c.it);
  }
  for (const [key, list] of A.auxs)
    if (!A.mains.has(key))
      for (const it of list) out.push({ sev: 'err', rule: "{0}: kontağın ana fonksiyonu (bobin, şalter vb.) yok", msg: $t("{0}: kontağın ana fonksiyonu (bobin, şalter vb.) yok", keyName(A, key)), it });
  for (const [key, list] of A.mains)
    if (list.length > 1)
      for (const it of list.slice(1))
        out.push({ sev: 'err', rule: "{0}: ana fonksiyon birden fazla kez kullanılmış (ilk: /{1})", msg: $t("{0}: ana fonksiyon birden fazla kez kullanılmış (ilk: /{1})", keyName(A, key), loc(list[0])), it });
  for (const [key, list] of A.mains)
    if (['coil', 'coil_on', 'coil_off'].includes(fnOf(list[0].s.type)) && !A.auxs.has(key)) out.push({ sev: 'warn', rule: "{0}: bobinin hiç kontağı yok", msg: $t("{0}: bobinin hiç kontağı yok", keyName(A, key)), it: list[0] });
  for (const [key, list] of A.solValves)
    if (!A.mains.has(key)) for (const it of list) out.push({ sev: 'warn', rule: "{0}: valf bobini {1} elektrik şemasında yok", msg: $t("{0}: valf bobini {1} elektrik şemasında yok", deviceName(A, it.s) || symbolName(it.s.type), A.devName.get(key) ?? key), it });
  for (const c of A.conflicts) out.push({ sev: 'err', rule: "Kısa devre: {0} ve {1} aynı potansiyelde birleşiyor", msg: $t("Kısa devre: {0} ve {1} aynı potansiyelde birleşiyor", c.a, c.b), it: c.it });

  const seenTerm = new Set<string>();
  for (const it of A.all) {
    const s = it.s, L = LIB[s.type];
    if (L.role === 'sl' || L.role === 'box') {
      if (L.role === 'sl' && s.tag && !A.mains.has(A.devKey.get(s.id) ?? ''))
        out.push({ sev: 'warn', rule: "{0}: tek hat gösteriminin çok hatlı şemada karşılığı (ana fonksiyon) yok", msg: $t("{0}: tek hat gösteriminin çok hatlı şemada karşılığı (ana fonksiyon) yok", deviceName(A, s)), it });
      continue;
    }
    if (s.type === 'cdp' && !A.pr.pages[it.pi].wires.some((w) => w.pts.slice(1).some((q, k) => onSeg(s.x, s.y, w.pts[k], q))))
      out.push({ sev: 'warn', rule: 'Bağlantı tanım noktası bir telin üzerinde değil', msg: $t('Bağlantı tanım noktası bir telin üzerinde değil'), it });
    if (!['text', 'pot', 'brk', 'deco'].includes(L.role) && !s.tag) out.push({ sev: 'warn', rule: "{0}: cihaz etiketi boş", msg: $t("{0}: cihaz etiketi boş", $t(L.name)), it });
    L.pins.forEach((pin, i) => {
      if (!isConnected(A, s, i)) out.push({ sev: 'warn', rule: "{0} bağlantısız", msg: $t("{0} bağlantısız", pinName(A, s, i)), it });
      // a protective earth terminal reaches the protective conductor, nothing else
      else if (pin[2] === 'PE') {
        const pot = potentialOf(A, pinNode(s, i));
        if (!pot) out.push({ sev: 'warn', rule: '{0}: koruma iletkenine (PE) ulaşmıyor', msg: $t('{0}: koruma iletkenine (PE) ulaşmıyor', pinName(A, s, i)), it });
        else if (pot.ptype && pot.ptype !== 'PE') out.push({ sev: 'err', rule: '{0}: PE ucu {1} potansiyeline bağlı', msg: $t('{0}: PE ucu {1} potansiyeline bağlı', pinName(A, s, i), pot.name || pot.ptype), it });
      }
    });
    if (['main', 'aux', 'term', 'plc'].includes(L.role)) {
      const pw = pinsW(s);
      // a connection point takes two conductors at most
      pw.forEach((p, i) => {
        const n = wiresAt(A, it.pi, p).reduce((a, x) => a + x.n, 0);
        if (n > 2) out.push({ sev: 'warn', rule: '{0}: {1} tel bağlı; bir bağlantı noktasına en fazla iki tel bağlanır', msg: $t('{0}: {1} tel bağlı; bir bağlantı noktasına en fazla iki tel bağlanır', pinName(A, s, i), n), it });
      });
      // the conductors on a protective device carry its current
      const how = PROTECTS[fnOf(s.type)], part = how && s.part ? parts(s.part) : undefined;
      const In = part ? (how === 'range' ? rangeOf(part.d)?.[0] : ratedOf(part.d)) : null;
      if (In) {
        const thin = pw.flatMap((p) => wiresAt(A, it.pi, p).map((x) => x.w)).filter((w) => !w.bus && Number(w.cs) > 0 && (CARRY.find(([c]) => c === Number(w.cs))?.[1] ?? Infinity) < In);
        if (thin.length) {
          const need = CARRY.find(([, a]) => a >= In)?.[0];
          out.push({ sev: 'warn', rule: '{0}: {1} mm² tel {2} A koruma için ince (en az {3} mm²)', msg: $t('{0}: {1} mm² tel {2} A koruma için ince (en az {3} mm²)', deviceName(A, s), String(Math.min(...thin.map((w) => Number(w.cs)))).replace('.', ','), In, need != null ? String(need).replace('.', ',') : '?'), it });
        }
      }
    }
    if (L.role === 'brk' && (A.brk.get(s.props.name || '') || []).length < 2)
      out.push({ sev: 'warn', rule: "Kesinti noktası \"{0}\" için karşı nokta yok", msg: $t("Kesinti noktası \"{0}\" için karşı nokta yok", s.props.name), it });
    if (L.role === 'term') {
      const k = A.devKey.get(s.id) + '|' + s.props.pinNo;
      if (seenTerm.has(k)) out.push({ sev: 'err', rule: "{0} klemens numarası tekrar ediyor", msg: $t("{0} klemens numarası tekrar ediyor", pinName(A, s, 0)), it });
      seenTerm.add(k);
    }
    const B = bbWorld(s);
    if (B[0] < SHEET.X0 || B[2] > SHEET.X1 || B[1] < SHEET.Y0 + SHEET.HDR || B[3] > SHEET.TB_Y)
      out.push({ sev: 'warn', rule: "{0} çizim alanının dışına taşıyor", msg: $t("{0} çizim alanının dışına taşıyor", L.role === 'text' ? 'Metin' : pinName(A, s, 0).split(':')[0]), it });
  }
  // plugs and their mating plugs: the same pins on both, a male one on a female one
  const plugs = plugsOf(A);
  for (const [key, list] of plugs) {
    const mate = list.find((it) => it.s.props.mate)?.s.props.mate?.replace(/^-/, '');
    if (!mate) continue;
    const other = [...plugs].find(([, l]) => l[0].s.tag === mate)?.[1];
    if (!other) {
      out.push({ sev: 'warn', rule: '{0}: karşı fiş -{1} projede yok', msg: $t('{0}: karşı fiş -{1} projede yok', keyName(A, key), mate), it: list[0] });
      continue;
    }
    const nos = new Set(other.map((it) => it.s.props.pinNo ?? ''));
    for (const it of list)
      if (!nos.has(it.s.props.pinNo ?? '')) out.push({ sev: 'warn', rule: '{0}: karşı fişte (-{1}) bu numarada pin yok', msg: $t('{0}: karşı fişte (-{1}) bu numarada pin yok', pinName(A, it.s, 0), mate), it });
    const gender = (l: Placed[]) => (l.every((it) => it.s.type === 'pin_m') ? 'm' : l.every((it) => it.s.type === 'pin_f') ? 'f' : '');
    if (gender(list) && gender(list) === gender(other)) out.push({ sev: 'warn', rule: '{0}: karşı fiş -{1} de aynı cinsten (ikisi de erkek ya da dişi)', msg: $t('{0}: karşı fiş -{1} de aynı cinsten (ikisi de erkek ya da dişi)', keyName(A, key), mate), it: list[0] });
  }
  out.push(...plcIssues(A, parts).map((m) => ({ ...m, rule: 'plc' })));
  out.push(...layoutIssues(A.pr, A, parts).map((m) => ({ ...m, rule: 'layout' })), ...panel3dIssues(A.pr, A, parts).map((m) => ({ ...m, rule: '3d' })));
  // keys in the order the messages are raised (stable while the drawing is), then the ones the user ignored left
  // out unless asked for
  const seen = new Map<string, number>();
  for (const m of out) {
    const base = `${hash(m.rule ?? m.msg)}|${m.it?.s.id ?? `p${m.pi ?? ''}`}`, n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    m.key = `${base}|${n}`;
  }
  const ignored = new Set(A.pr.ignoredChecks ?? []);
  return out.filter((m) => o.ignored || !ignored.has(m.key!)).sort((a, b) => (a.sev === 'err' ? 0 : 1) - (b.sev === 'err' ? 0 : 1));
}

export type ReportTab = 'dev' | 'bom' | 'con' | 'wlen' | 'ter' | 'plg' | 'cab' | 'cabc' | 'plc' | 'drl' | 'cu' | 'ver' | 'rev' | 'chk';
export const REPORT_TABS: [ReportTab, string][] = [
  ['dev', tk("Cihaz listesi")],
  ['bom', tk("Malzeme listesi")],
  ['con', tk("Bağlantı listesi")],
  ['wlen', tk('Pano tel listesi')],
  ['ter', tk("Klemens planı")],
  ['plg', tk('Fiş planı')],
  ['cab', tk("Kablo listesi")],
  ['cabc', tk("Kablo bağlantı planı")],
  ['plc', tk('PLC I/O listesi')],
  ['drl', tk("Kapak delik listesi")],
  ['cu', tk('Bakır kesim listesi')],
  ['ver', tk('Pano doğrulama')],
  ['rev', tk('Revizyon listesi')],
  ['chk', tk('Kontrol')],
];

export interface ReportRow {
  cells: string[];
  /** page to jump to when there is no target symbol */
  page?: number;
  /** symbol to jump to */
  target?: Placed;
  sev?: Severity;
  /** group header row (e.g. terminal strip) */
  group?: boolean;
  /** check messages: the key to ignore it by, and whether it is ignored */
  key?: string;
  ignored?: boolean;
}
export interface Report {
  title: string;
  sub: string;
  cols: string[];
  /** indexes of columns set in monospace */
  mono: number[];
  rows: ReportRow[];
}

const tagSort = (a: string, b: string) => a.localeCompare(b, 'tr', { numeric: true });
const skipInLists = (role: string) => ['brk', 'pot', 'text', 'sl', 'box'].includes(role);

export function buildReport(pr: Project, A: Analysis, tab: ReportTab, parts: PartLookup = catOf): Report {
  if (tab === 'dev') {
    const m = new Map<string, Placed[]>();
    for (const it of A.all) {
      const L = LIB[it.s.type];
      if (!['main', 'aux', 'plc'].includes(L.role) || !it.s.tag) continue;
      const key = A.devKey.get(it.s.id)!;
      (m.get(key) || m.set(key, []).get(key)!).push(it);
    }
    const rows = [...m.entries()]
      .sort((a, b) => tagSort(keyName(A, a[0]), keyName(A, b[0])))
      .map(([key, list]) => {
        list.sort(placedOrder);
        const main = list.find((x) => LIB[x.s.type].role !== 'aux') || list[0];
        const c = parts(main.s.part);
        return {
          target: main,
          cells: [keyName(A, key), symbolName(main.s.type), main.s.desc, main.s.part, partText(c), main.s.maker, list.map((x) => '/' + loc(x)).join(' ')],
        };
      });
    return {
      title: $t("Cihaz listesi"),
      sub: $t("Her cihaz etiketi bir satır. Satıra tıklayınca şemada gösterilir."),
      cols: ['Etiket', 'Fonksiyon', $t("Açıklama"), $t("Parça no"), $t("Parça tanımı"), $t("Üretici"), 'Konumlar'],
      mono: [0, 3, 6],
      rows,
    };
  }

  if (tab === 'bom') {
    const m = new Map<string, { n: number; tags: string[]; it: Placed; maker: string }>();
    // a part and, recursively, the accessories that come with it
    const add = (pn: string, n: number, tag: string, it: Placed, maker: string, depth: number) => {
      let e = m.get(pn);
      if (!e) m.set(pn, (e = { n: 0, tags: [], it, maker }));
      e.n += n;
      e.tags.push(tag);
      if (depth < 4) for (const a of parts(pn)?.acc ?? []) if (a.pn && a.n > 0) add(a.pn, n * a.n, tag, it, '', depth + 1);
    };
    for (const it of A.all) {
      const s = it.s;
      if (!s.part || ['aux', 'sl', 'box'].includes(LIB[s.type].role)) continue;
      add(s.part, 1, LIB[s.type].role === 'term' ? pinName(A, s, 0) : deviceName(A, s), it, s.maker, 0);
    }
    const money = (v: number) => v.toLocaleString(getLang(), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const totals = new Map<string, number>();
    let unpriced = 0, kg = 0, unweighed = 0;
    const rows: ReportRow[] = [...m.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([pn, e], i) => {
        const p = parts(pn), n = Math.round(e.n * 1000) / 1000;
        if (p?.kg != null) kg += p.kg * n;
        else unweighed++;
        let unit = '', sum = '';
        if (p?.price != null) {
          const cur = p.cur || 'EUR';
          unit = `${money(p.price)} ${cur}`;
          sum = `${money(p.price * n)} ${cur}`;
          totals.set(cur, (totals.get(cur) ?? 0) + p.price * n);
        } else unpriced++;
        return {
          target: e.it,
          cells: [String(i + 1), pn, partText(p), e.maker || p?.maker || MAKER, String(n).replace('.', ','), unit, sum, [...new Set(e.tags)].join(', ')],
        };
      });
    // mounting material from the panel layouts: rails in 1 m, ducts in 2 m lengths
    const mat = layoutMaterials(pr, A, parts), m1 = (mm: number) => (mm / 1000).toFixed(2).replace('.', ',');
    if (mat.rail) rows.push({ cells: [String(rows.length + 1), 'DIN-35-1M', $t("DIN rayı 35 mm · toplam {0} m", m1(mat.rail)), parts('DIN-35-1M')?.maker || MAKER, String(Math.ceil(mat.rail / 1000)), '', '', $t("pano yerleşimi")] });
    for (const [size, len] of mat.copper)
      rows.push({ cells: [String(rows.length + 1), `CU-${size}`, $t('Bakır bara {0} mm · toplam {1} m (ana baralar, şalter ayakları, inişler ve bağlantı baraları)', size, m1(len)), MAKER, String(Math.ceil(len / 1000)), '', '', $t("pano yerleşimi")] });
    if (mat.supports)
      rows.push({ cells: [String(rows.length + 1), 'MSN', $t('Bara mesnedi · faz baraları ve N / PE için (çift)'), MAKER, String(mat.supports), '', '', $t('pano yerleşimi')] });
    for (const [size, len] of mat.ducts)
      rows.push({ cells: [String(rows.length + 1), `KK-${size}`, $t("Kablo kanalı {0} mm · toplam {1} m (2 m boy)", size, m1(len)), MAKER, String(Math.ceil(len / 2000)), '', '', $t("pano yerleşimi")] });
    // copper: 8,96 kg per dm³ → kg per metre of a w×t mm bar is w·t·0,00896
    for (const [size, len] of mat.copper) {
      const [bw, bt] = size.split('×').map(Number);
      kg += (bw * bt * 0.00896 * len) / 1000;
    }
    if (kg > 0)
      rows.push({ group: true, cells: [$t('Cihaz ağırlığı ≈ {0} kg (pano gövdesi hariç)', Math.round(kg).toLocaleString(getLang())) + (unweighed ? ' · ' + $t('{0} kalemin ağırlığı yok', unweighed) : '')] });
    for (const [cur, v] of [...totals].sort())
      rows.push({ group: true, cells: [$t('Toplam ({0}): {1}', cur, `${money(v)} ${cur}`) + (unpriced ? ' · ' + $t('{0} kalemin fiyatı yok', unpriced) : '')] });
    return {
      title: $t("Malzeme listesi"),
      sub: $t('Parça numarası atanmış cihazlar ve aksesuarları, adet ve fiyatlarıyla.'),
      cols: ['Poz', $t("Parça no"), $t("Tanım"), $t("Üretici"), 'Adet', $t('Birim fiyat'), $t('Tutar'), 'Etiketler'],
      mono: [1, 4, 5, 6, 7],
      rows,
    };
  }

  if (tab === 'wlen') {
    // wires between devices on each mounting plate, routed through the ducts, with cut lengths
    const rows: ReportRow[] = [];
    const totals = new Map<string, { n: number; len: number }>();
    pr.pages.forEach((p, pi) => {
      if (p.kind !== 'layout' || !p.layout || p.layout.door) return;
      const ws = cabinetWiring(pr, A, pi, parts);
      if (!ws.length) return;
      rows.push({ group: true, page: pi, cells: [`${pi + 1} · ${p.desc}`] });
      for (const w of ws.sort((a, b) => a.no.localeCompare(b.no, 'tr', { numeric: true }))) {
        rows.push({ page: pi, cells: [w.no || '—', w.from, w.to, w.cs ? w.cs + ' mm²' : '', w.col, String(w.len)] });
        const k = `${w.cs || '?'} mm² ${w.col || '—'}`;
        const t = totals.get(k) ?? { n: 0, len: 0 };
        t.n++;
        t.len += w.len;
        totals.set(k, t);
      }
    });
    for (const [k, t] of [...totals].sort())
      rows.push({ group: true, cells: [$t('Toplam {0}: {1} tel · {2} m', k, t.n, (t.len / 1000).toFixed(1).replace('.', ','))] });
    return {
      title: $t('Pano tel listesi'),
      sub: $t('Montaj plakasındaki cihazlar arasındaki teller, kablo kanallarından geçen yolları ve her uçta {0} mm pay ile kesim boyları. Cihazların pano yerleşiminde olması gerekir.', WIRE_RESERVE),
      cols: ['Tel', 'Kaynak', 'Hedef', 'Kesit', 'Renk', $t('Uzunluk (mm)')],
      mono: [0, 1, 2, 5],
      rows,
    };
  }

  if (tab === 'con') {
    const nets = new Map<string, { it: Placed; i: number }[]>();
    for (const it of A.all) {
      const L = LIB[it.s.type];
      if (skipInLists(L.role)) continue;
      L.pins.forEach((_, i) => {
        if (!isConnected(A, it.s, i)) return;
        const r = A.uf.find(pinNode(it.s, i));
        (nets.get(r) || nets.set(r, []).get(r)!).push({ it, i });
      });
    }
    const wireOf = new Map<string, Wire>();
    pr.pages.forEach((p) =>
      p.wires.forEach((w) => {
        const r = A.uf.find(wireNode(w.id));
        if (!wireOf.has(r)) wireOf.set(r, w);
      }),
    );
    const rows: ReportRow[] = [];
    [...nets.entries()]
      .sort((a, b) => (A.netNo.get(a[0]) ?? 1e9) - (A.netNo.get(b[0]) ?? 1e9))
      .forEach(([r, list]) => {
        list.sort((a, b) => a.it.pi - b.it.pi || a.it.s.x - b.it.s.x || pinsW(a.it.s)[a.i][1] - pinsW(b.it.s)[b.i][1]);
        const p = potentialOf(A, pinNode(list[0].it.s, list[0].i));
        const w = wireOf.get(r);
        for (let i = 0; i < list.length - 1; i++) {
          const a = list[i], b = list[i + 1];
          rows.push({
            target: a.it,
            cells: [
              A.netLabel.get(r) ?? '—', p ? p.name || p.ptype : '',
              pinName(A, a.it.s, a.i), '/' + loc(a.it), pinName(A, b.it.s, b.i), '/' + loc(b.it),
              w?.bus ? `Cu ${w.bus}` : w?.cs ? w.cs + ' mm²' : '', w?.col || '',
            ],
          });
        }
      });
    return {
      title: $t("Bağlantı listesi"),
      sub: $t("Aynı teldeki cihaz uçları sırayla eşlenir. Kablolama ve tel etiketleri için kullanılabilir."),
      cols: ['Tel', 'Potansiyel', 'Kaynak', 'Konum', 'Hedef', 'Konum', 'Kesit', 'Renk'],
      mono: [0, 1, 2, 3, 4, 5],
      rows,
    };
  }

  if (tab === 'plg') {
    // plugs and sockets: per plug its kind and mating plug, pin by pin what is connected
    const kindName = (t: string) => (t === 'pin_m' ? $t('Erkek') : t === 'pin_f' ? $t('Dişi') : $t('Fiş-soket'));
    const rows: ReportRow[] = [];
    for (const [key, list] of [...plugsOf(A)].sort((a, b) => tagSort(keyName(A, a[0]), keyName(A, b[0])))) {
      const mate = list.find((it) => it.s.props.mate)?.s.props.mate;
      const kinds = [...new Set(list.map((it) => kindName(it.s.type)))].join(' / ');
      rows.push({ group: true, cells: [`${keyName(A, key)} · ${kinds}${mate ? ` · ${$t('karşı fiş -{0}', mate.replace(/^-/, ''))}` : ''}`] });
      for (const it of [...list].sort((a, b) => tagSort(String(a.s.props.pinNo), String(b.s.props.pinNo)))) {
        const p = potentialOf(A, pinNode(it.s, 0));
        const conn = LIB[it.s.type].pins.map((_, i) => connectedTo(A, it, i)).filter(Boolean).join(' | ');
        rows.push({ target: it, cells: [it.s.props.pinNo || '?', kindName(it.s.type), conn, p ? p.name || p.ptype : '', mate ? `-${mate.replace(/^-/, '')}:${it.s.props.pinNo || '?'}` : '', it.s.part, '/' + loc(it)] });
      }
    }
    return {
      title: $t('Fiş planı'),
      sub: $t('Fiş ve soketler pin pin: türü, bağlı olduğu uçlar ve karşı fişteki eşi.'),
      cols: ['Pin', $t('Tür'), $t('Bağlantı'), $t('Potansiyel'), $t('Karşı fiş pini'), $t('Parça no'), 'Konum'],
      mono: [0, 2, 3, 4, 5, 6],
      rows,
    };
  }

  if (tab === 'ter') {
    const strips = new Map<string, Placed[]>();
    for (const it of A.all)
      if (LIB[it.s.type].role === 'term' && !PLUG_TYPES.has(it.s.type)) {
        const key = A.devKey.get(it.s.id) ?? '-?';
        (strips.get(key) || strips.set(key, []).get(key)!).push(it);
      }
    const others = (it: Placed, i: number) => {
      const r = A.uf.find(pinNode(it.s, i)), o: string[] = [];
      for (const x of A.all) {
        const role = LIB[x.s.type].role;
        if (x.s.id === it.s.id || role === 'brk' || role === 'text') continue;
        LIB[x.s.type].pins.forEach((_, j) => {
          if (A.uf.find(pinNode(x.s, j)) === r) o.push(pinName(A, x.s, j));
        });
      }
      return o.join(', ');
    };
    const rows: ReportRow[] = [];
    [...strips.keys()].sort((a, b) => tagSort(keyName(A, a), keyName(A, b))).forEach((key) => {
      rows.push({ group: true, cells: [keyName(A, key)] });
      strips
        .get(key)!
        .sort((a, b) => tagSort(String(a.s.props.pinNo), String(b.s.props.pinNo)))
        .forEach((it) => {
          const p = potentialOf(A, pinNode(it.s, 0));
          rows.push({ target: it, cells: [it.s.props.pinNo || '?', others(it, 0), others(it, 1), p ? p.name || p.ptype : '', it.s.part, '/' + loc(it)] });
        });
    });
    return {
      title: $t("Klemens planı"),
      sub: $t("Üst uç pano içi, alt uç saha tarafı kabul edilir."),
      cols: ['Klemens', $t("İç bağlantı (üst)"), $t("Dış bağlantı (alt)"), 'Potansiyel', $t("Parça no"), 'Konum'],
      mono: [0, 1, 2, 3, 4, 5],
      rows,
    };
  }

  if (tab === 'cab' || tab === 'cabc') return cableReport(A, tab);
  if (tab === 'rev') {
    const idx = new Map(pr.pages.map((p, i) => [p.id, i]));
    const list = pr.revisions?.length ? pr.revisions : pr.revision ? [pr.revision] : [];
    return {
      title: $t('Revizyon listesi'), sub: $t('Adlı revizyonlar ve her birinin değiştirdiği şema sayfaları. Sayfanın antedinde onu son değiştiren revizyon görünür.'),
      cols: [$t('Revizyon'), $t('Tarih'), $t('Hazırlayan'), $t('Açıklama'), $t('Değişen sayfalar')], mono: [0, 1, 4],
      rows: [...list].reverse().map((r) => {
        const pages = (r.pages ?? []).map((id) => idx.get(id)).filter((i): i is number => i != null).map((i) => i + 1);
        return { page: pages.length ? pages[0] - 1 : undefined, cells: [r.label, r.date, r.by ?? '', r.note ?? '', pages.length ? pages.join(', ') : '—'] };
      }),
    };
  }
  if (tab === 'drl') {
    const nf = (v: number) => String(v).replace('.', ',');
    return {
      title: $t("Kapak delik listesi"), sub: $t("Pano kapağı sayfalarındaki kesimler; merkez ölçüleri kapağın sol üst köşesinden, mm."),
      cols: ['Sayfa', 'Cihaz', $t("Açıklama"), $t("Kesim (mm)"), 'X (mm)', 'Y (mm)'], mono: [0, 1, 3, 4, 5],
      rows: doorHoles(pr, A, parts).map((h) => ({ page: h.pi, cells: [String(h.pi + 1), h.name, h.desc, cutText(h.cut) || $t("üretici verisi"), nf(h.x), nf(h.y)] })),
    };
  }
  if (tab === 'ver') {
    const names: Record<string, string> = { ok: $t('Uygun'), check: $t('Kontrol edin'), fail: $t('Uygun değil'), verify: $t('Doğrulanmalı') };
    return {
      title: $t('Pano doğrulama'),
      sub: $t('IEC 61439-1/-2 madde 10 tasarım doğrulaması: çizim ve yerleşimden gösterilebilenler kontrol edildi; test ya da üretici verisi gerekenler "doğrulanmalı" olarak listelendi.'),
      cols: ['Durum', $t('Madde'), $t('Konu'), $t('Ayrıntı')],
      mono: [1],
      rows: boardVerification(pr, A, parts, runChecks(A, parts)).map((v) => ({
        page: v.pi,
        ...(v.status === 'fail' ? { sev: 'err' as const } : v.status === 'check' ? { sev: 'warn' as const } : {}),
        cells: [names[v.status], v.clause, v.topic, v.detail],
      })),
    };
  }
  if (tab === 'cu') {
    const list = copperCutList(pr, A, parts), nf = (v: number) => v.toLocaleString(getLang());
    const rows: ReportRow[] = list.map((c) => ({ page: c.pi, cells: [String(c.pi + 1), c.part, c.ph, c.size, String(c.count), String(c.bends), nf(c.straight), nf(c.cut)] }));
    // per bar size: the total to order
    const tot = new Map<string, number>();
    for (const c of list) tot.set(c.size, (tot.get(c.size) ?? 0) + c.cut * c.count);
    for (const [size, mm] of [...tot].sort()) rows.push({ group: true, cells: [$t('Cu {0}: toplam {1} m kesim boyu', size, (mm / 1000).toFixed(2).replace('.', ','))] });
    return {
      title: $t('Bakır kesim listesi'),
      sub: $t('Pano yerleşimindeki bakır parçaları ve kesim (açınım) boyları: 90° bükümlerde iç yarıçap bakır kalınlığı, büküm başına (2 − π/2)·(r + t/2) kısalır.'),
      cols: ['Sayfa', $t('Parça'), 'Faz', $t('Bakır'), 'Adet', $t('Büküm'), $t('Düz ölçü (mm)'), $t('Kesim boyu (mm)')],
      mono: [0, 3, 4, 5, 6, 7],
      rows,
    };
  }
  if (tab === 'plc')
    return {
      title: $t('PLC I/O listesi'), sub: $t("Kart ve adres sırasıyla tüm PLC kanalları ve bağlı oldukları cihazlar."),
      cols: ['Kart', 'Adres', $t("Tür"), $t('Sembol'), $t("Açıklama"), $t("Bağlı olduğu uç"), $t("Parça no"), 'Konum'], mono: [0, 1, 2, 3, 5, 6, 7],
      rows: plcList(A).map((r) => ({ target: r.it, cells: r.cells })),
    };

  const all = runChecks(A, parts, { ignored: true }), ign = new Set(A.pr.ignoredChecks ?? []);
  const cs = all.filter((c) => !ign.has(c.key!)), off = all.filter((c) => ign.has(c.key!));
  const row = (c: CheckMessage, ignored = false): ReportRow => ({ target: c.it, page: c.pi, sev: c.sev, key: c.key, ...(ignored ? { ignored } : {}), cells: [c.sev === 'err' ? 'Hata' : $t("Uyarı"), c.msg, checkLocation(c)] });
  return {
    title: 'Kontrol',
    sub: cs.length ? $t("Şemadaki tutarsızlıklar. Satıra tıklayınca ilgili sembol seçilir.") : $t("Hata ya da uyarı bulunmadı."),
    cols: ['Durum', 'Mesaj', 'Konum'],
    mono: [2],
    rows: [...cs.map((c) => row(c)), ...(off.length ? [{ group: true, cells: [$t('Yok sayılanlar ({0})', off.length)] }, ...off.map((c) => row(c, true))] : [])],
  };
}

/** Semicolon-separated CSV (opens directly in Turkish-locale Excel). */
export function reportToCsv(r: Report): string {
  const q = (v: string) => (/[;"\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v);
  return [r.cols, ...r.rows.map((x) => x.cells)].map((row) => row.map((c) => q(String(c ?? ''))).join(';')).join('\r\n');
}

/** Pins of a net on the same page as the cable, split by side of the cable line. */
function coreEnds(A: Analysis, cable: Placed, wireId: string) {
  const r = A.uf.find(wireNode(wireId));
  const s = cable.s, horiz = s.rot % 2 === 0;
  const src: string[] = [], dst: string[] = [];
  for (const it of A.all) {
    const role = LIB[it.s.type].role;
    if (skipInLists(role) || role === 'cable') continue;
    pinsW(it.s).forEach((p, i) => {
      if (A.uf.find(pinNode(it.s, i)) !== r) return;
      const name = pinName(A, it.s, i) + (it.pi !== cable.pi ? ` /${loc(it)}` : '');
      if (it.pi !== cable.pi) return void dst.push(name);
      const before = horiz ? p[1] < s.y : p[0] < s.x;
      (before ? src : dst).push(name);
    });
  }
  return { src: src.join(', '), dst: dst.join(', ') };
}

function cableReport(A: Analysis, tab: 'cab' | 'cabc'): Report {
  const list = [...A.cables.values()].sort((a, b) => tagSort(deviceName(A, a.it.s), deviceName(A, b.it.s)));
  if (tab === 'cab') {
    const rows = list.map((c) => {
      const s = c.it.s, src = new Set<string>(), dst = new Set<string>();
      for (const x of c.cross) {
        const e = coreEnds(A, c.it, x.wireId);
        e.src.split(', ').filter(Boolean).forEach((v) => src.add(v.split(':')[0]));
        e.dst.split(', ').filter(Boolean).forEach((v) => dst.add(v.split(':')[0]));
      }
      return {
        target: c.it,
        cells: [deviceName(A, s), s.props.cableType || '', `${c.cores.length}×${s.props.cs || '?'}`, s.props.shield === '1' ? $t('var') : '', `${c.cross.filter((x) => x.core).length}/${c.cores.length}`,
          [...src].join(', '), [...dst].join(', '), s.props.length ? s.props.length + ' m' : '', s.part, '/' + loc(c.it)],
      };
    });
    return {
      title: $t("Kablo listesi"), sub: $t("Her kablo bir satır. Kaynak kablonun üst/sol tarafındaki, hedef alt/sağ tarafındaki cihazlardır."),
      cols: ['Kablo', 'Tip', $t("Damar × kesit"), $t('Ekran'), $t("Kullanılan"), 'Kaynak', 'Hedef', 'Uzunluk', $t('Parça no'), 'Konum'], mono: [0, 2, 4, 5, 6, 8, 9], rows,
    };
  }
  const rows: ReportRow[] = [];
  for (const c of list) {
    const s = c.it.s;
    rows.push({ group: true, cells: [`${deviceName(A, s)}  ${s.props.cableType || ''} ${c.cores.length}×${s.props.cs || '?'}${s.props.shield === '1' ? ' +SH' : ''}`] });
    const used = new Map(c.cross.filter((x) => x.core).map((x) => [x.core, x]));
    for (const core of c.cores) {
      const x = used.get(core);
      if (!x) {
        rows.push({ target: c.it, cells: [core, '', '', '', 'yedek'] });
        continue;
      }
      const e = coreEnds(A, c.it, x.wireId), p = potentialOf(A, wireNode(x.wireId));
      rows.push({ target: c.it, cells: [core, e.src, e.dst, p ? p.name || p.ptype : '', netLabelOf(A, wireNode(x.wireId))] });
    }
    if (s.props.shield === '1') rows.push({ target: c.it, cells: ['SH', '', '', 'PE', $t('Ekran')] });
  }
  return {
    title: $t("Kablo bağlantı planı"), sub: $t("Her damarın bağlandığı uçlar. Kullanılmayan damarlar \"yedek\" olarak görünür."),
    cols: ['Damar', 'Kaynak', 'Hedef', 'Potansiyel', $t("Tel no")], mono: [0, 1, 2, 3, 4], rows,
  };
}

/* ---------------- marking list for label printers ---------------- */

export type MarkKind = 'wire' | 'terminal' | 'device' | 'cable';

export interface MarkOptions {
  wires: boolean;
  /** wire marker text: the wire number only, or the number with the far end of that wire */
  wireText: 'no' | 'dest';
  terminals: boolean;
  devices: boolean;
  cables: boolean;
  /** same text of the same kind once, with a count, instead of one row per label */
  merge: boolean;
}

export interface Mark {
  kind: MarkKind;
  text: string;
  /** terminal strip, wire potential, cable type: for grouping in the label software */
  group: string;
  n: number;
}

/**
 * Labels for a label printer, one row per label (or per text with a count): wire markers
 * for both ends of every wire, terminal strip and terminal numbers, device tags, and cable
 * tags for both cable ends. Brand neutral; the label software imports it as CSV or text.
 */
export function markingList(pr: Project, A: Analysis, o: MarkOptions): Mark[] {
  const out: Mark[] = [];
  const add = (kind: MarkKind, text: string, group = '') => text && out.push({ kind, text, group, n: 1 });
  if (o.wires) {
    // the wires of the connection list: consecutive pins of a net, a marker at each end
    const R = buildReport(pr, A, 'con');
    for (const r of R.rows) {
      const [no, pot, from, , to] = r.cells;
      if (!no || no === '—') continue;
      add('wire', o.wireText === 'dest' ? `${no} ${to}` : no, pot);
      add('wire', o.wireText === 'dest' ? `${no} ${from}` : no, pot);
    }
  }
  if (o.terminals) {
    let strip = '';
    for (const r of buildReport(pr, A, 'ter').rows) {
      if (r.group) add('terminal', (strip = r.cells[0]), strip);
      else add('terminal', r.cells[0], strip);
    }
  }
  if (o.devices)
    for (const r of buildReport(pr, A, 'dev').rows) add('device', r.cells[0], r.cells[2] ?? '');
  if (o.cables)
    for (const c of [...A.cables.values()].sort((a, b) => tagSort(deviceName(A, a.it.s), deviceName(A, b.it.s)))) {
      const name = deviceName(A, c.it.s), type = c.it.s.props.cableType || '';
      add('cable', name, type);
      add('cable', name, type);
    }
  if (!o.merge) return out;
  const m = new Map<string, Mark>();
  for (const x of out) {
    const k = `${x.kind}\u0000${x.text}`;
    const e = m.get(k);
    if (e) e.n++;
    else m.set(k, { ...x });
  }
  return [...m.values()];
}

export const MARK_KINDS: Record<MarkKind, string> = { wire: tk('Tel'), terminal: tk('Klemens'), device: tk('Cihaz'), cable: tk('Kablo') };

/** CSV (semicolon separated, as Excel in most locales expects) with a count column. */
export function marksToCsv(marks: Mark[]): string {
  return reportToCsv({ title: '', sub: '', mono: [], cols: [$t('Tür'), $t('Metin'), $t('Grup'), $t('Adet')], rows: marks.map((x) => ({ cells: [$t(MARK_KINDS[x.kind]), x.text, x.group, String(x.n)] })) });
}

/** Plain text, one label per line (a merged text repeated by its count): the lowest common format. */
export function marksToText(marks: Mark[]): string {
  return marks.flatMap((x) => Array<string>(x.n).fill(x.text)).join('\r\n') + '\r\n';
}
